import { useCallback, useEffect, useState } from 'react';
import { api } from '../api/cliente';
import type {
  ConsumoDeTecnico, Material, MovimientoMaterial, Opcion, Pagina,
  ResumenMateriales, SolicitudMaterial,
} from '../api/tipos';
import { useSesion } from '../sesion';
import {
  Cargando, Dialogo, Error, Ordenable, Paginacion, useOrden,
} from '../componentes/comunes';

/**
 * Materiales: el otro inventario de almacén.
 *
 * Las refacciones se le montan a un equipo y salen contra una orden de
 * trabajo. Los materiales son los insumos que se le entregan al técnico
 * —consumibles, herramienta menor, lo que se gasta trabajando— y no
 * cuelgan de ninguna orden: se piden para una persona y salen a su
 * nombre.
 *
 * La pantalla es de dos oficios a la vez, y por eso las pestañas están en
 * este orden: quien pide sólo entra a «Solicitudes» y no le interesa el
 * resto; quien surte vive ahí también, y de ahí pasa al catálogo o al
 * kardex cuando hace falta. Lo primero que se ve es el trabajo, no la
 * consulta.
 */

function moneda(valor: number): string {
  return valor.toLocaleString('es-MX', { style: 'currency', currency: 'MXN' });
}

function fechaCorta(iso: string): string {
  return new Date(iso).toLocaleDateString('es-MX', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
}

const ETIQUETA_ESTATUS: Record<string, string> = {
  en_revision: 'Por atender',
  disponible: 'Con existencia',
  parcial: 'Surtida en parte',
  sin_stock: 'Sin existencia',
  surtida: 'Entregada',
  cancelada: 'Cancelada',
};

const ETIQUETA_MOTIVO: Record<string, string> = {
  compra_proveedor: 'Compra a proveedor',
  salida_tecnico: 'Entrega a técnico',
  devolucion_tecnico: 'Devolución del técnico',
  transferencia_bodegas: 'Transferencia',
  ajuste_inventario: 'Ajuste por conteo',
  inventario_inicial: 'Inventario inicial',
  otro: 'Otro',
};

function Tarjeta({ titulo, valor, pie, alerta }: {
  titulo: string; valor: string; pie?: string; alerta?: boolean;
}) {
  return (
    <div className="tarjeta" style={{ cursor: 'default' }}>
      <div className="conteo">{titulo}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: alerta ? '#b02a37' : undefined }}>
        {valor}
      </div>
      {pie && <div className="conteo">{pie}</div>}
    </div>
  );
}

export default function Materiales() {
  const { puede } = useSesion();
  const [vista, setVista] = useState<'solicitudes' | 'catalogo' | 'movimientos' | 'consumo'>('solicitudes');
  const [resumen, setResumen] = useState<ResumenMateriales | null>(null);
  const [recarga, setRecarga] = useState(0);

  // Surtir y ajustar mueven el inventario; pedir, no. Es la línea que
  // separa a almacén de quien pide, y la que hizo falta un módulo propio
  // para poder trazar.
  const surte = puede('materiales', 'editar');
  const pide = puede('materiales', 'crear');

  useEffect(() => {
    api.get<ResumenMateriales>('/materiales/resumen')
      .then(setResumen)
      .catch(() => undefined);
  }, [recarga]);

  return (
    <>
      <div className="encabezado">
        <div>
          <h1>Materiales</h1>
          <p className="sub">
            Los insumos que se le entregan al técnico. No cuelgan de una
            orden: se piden para una persona y salen a su nombre.
          </p>
        </div>
      </div>

      {resumen && (
        <div className="rejilla-catalogos">
          <Tarjeta
            titulo="Solicitudes por atender"
            valor={`${resumen.solicitudesPendientes}`}
            alerta={resumen.solicitudesPendientes > 0}
          />
          <Tarjeta
            titulo="Referencias con existencia"
            valor={`${resumen.conStock}`}
            pie={`de ${resumen.referencias} en catálogo`}
          />
          <Tarjeta titulo="Valor del inventario" valor={moneda(resumen.valor)} pie="a costo promedio" />
          <Tarjeta
            titulo="Por reponer"
            valor={`${resumen.porReponer}`}
            pie="bajo su mínimo"
            alerta={resumen.porReponer > 0}
          />
        </div>
      )}

      <nav className="submenu">
        <button
          className={vista === 'solicitudes' ? 'activo' : ''}
          onClick={() => setVista('solicitudes')}
        >
          Solicitudes
        </button>
        <button
          className={vista === 'catalogo' ? 'activo' : ''}
          onClick={() => setVista('catalogo')}
        >
          Catálogo
        </button>
        <button
          className={vista === 'movimientos' ? 'activo' : ''}
          onClick={() => setVista('movimientos')}
        >
          Movimientos
        </button>
        <button
          className={vista === 'consumo' ? 'activo' : ''}
          onClick={() => setVista('consumo')}
        >
          Consumo por técnico
        </button>
      </nav>

      {vista === 'solicitudes' && (
        <Solicitudes
          surte={surte}
          pide={pide}
          onCambio={() => setRecarga((n) => n + 1)}
        />
      )}
      {vista === 'catalogo' && (
        <Catalogo surte={surte} onCambio={() => setRecarga((n) => n + 1)} />
      )}
      {vista === 'movimientos' && <Movimientos />}
      {vista === 'consumo' && <Consumo />}
    </>
  );
}

// ═══════════════════════════════════════════════════════ solicitudes

function Solicitudes({ surte, pide, onCambio }: {
  surte: boolean; pide: boolean; onCambio: () => void;
}) {
  const [pagina, setPagina] = useState<Pagina<SolicitudMaterial> | null>(null);
  const [numero, setNumero] = useState(1);
  const [pendientes, setPendientes] = useState(true);
  const [busqueda, setBusqueda] = useState('');
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [abierta, setAbierta] = useState<SolicitudMaterial | null>(null);
  const [creando, setCreando] = useState(false);

  const cargar = useCallback(() => {
    setCargando(true);
    setError(null);
    api.get<Pagina<SolicitudMaterial>>('/materiales/solicitudes', {
      pendientes: pendientes ? 'si' : undefined,
      busqueda: busqueda || undefined,
      pagina: numero,
      limite: 25,
    })
      .then(setPagina)
      .catch(setError)
      .finally(() => setCargando(false));
  }, [pendientes, busqueda, numero]);

  useEffect(() => {
    const t = setTimeout(cargar, busqueda ? 300 : 0);
    return () => clearTimeout(t);
  }, [cargar, busqueda]);

  function refrescar() {
    cargar();
    onCambio();
  }

  return (
    <div className="panel">
      <div className="controles">
        <input
          type="search"
          placeholder="Folio o técnico…"
          value={busqueda}
          onChange={(e) => { setBusqueda(e.target.value); setNumero(1); }}
        />
        <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <input
            type="checkbox"
            checked={pendientes}
            onChange={(e) => { setPendientes(e.target.checked); setNumero(1); }}
          />
          Sólo las que faltan por atender
        </label>
        {pide && (
          <button
            className="primario"
            style={{ marginLeft: 'auto' }}
            onClick={() => setCreando(true)}
          >
            Pedir material
          </button>
        )}
      </div>

      <Error error={error} />

      {cargando && !pagina ? <Cargando /> : pagina && (
        <>
          {pagina.data.length === 0 ? (
            <div className="vacio">No hay solicitudes con ese filtro.</div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Folio</th>
                  <th>Para</th>
                  <th>Qué pidió</th>
                  <th className="numero">Falta</th>
                  <th>Estatus</th>
                  <th>Pedida</th>
                </tr>
              </thead>
              <tbody>
                {pagina.data.map((s) => {
                  const falta = s.partidas.reduce((t, p) => t + p.faltante, 0);

                  return (
                    <tr key={s.id}>
                      <td className="mono">
                        <button className="enlace" onClick={() => setAbierta(s)}>{s.folio}</button>
                      </td>
                      <td>{s.tecnico?.nombre_completo ?? '—'}</td>
                      <td style={{ maxWidth: 320 }}>
                        {s.partidas
                          .map((p) => p.material?.nombre ?? p.descripcionLibre)
                          .join(', ')}
                      </td>
                      <td className="numero">
                        {falta > 0 ? <span className="etiqueta no">{falta}</span> : '—'}
                      </td>
                      <td>
                        <span className={s.estatus === 'surtida' ? 'etiqueta si' : 'etiqueta no'}>
                          {ETIQUETA_ESTATUS[s.estatus] ?? s.estatus}
                        </span>
                      </td>
                      <td>{fechaCorta(s.creadoEn)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          <Paginacion
            pagina={pagina.pagina} limite={pagina.limite} total={pagina.total}
            onCambiar={setNumero}
          />
        </>
      )}

      {creando && (
        <PedirMaterial
          onCerrar={() => setCreando(false)}
          onHecho={() => { setCreando(false); refrescar(); }}
        />
      )}

      {abierta && (
        <FichaSolicitud
          solicitud={abierta}
          surte={surte}
          onCerrar={() => setAbierta(null)}
          onHecho={() => { setAbierta(null); refrescar(); }}
        />
      )}
    </div>
  );
}

/** Un renglón mientras se captura la solicitud. */
interface RenglonPedido {
  llave: number;
  material: { id: string; codigo: string; nombre: string; disponible: number } | null;
  descripcionLibre: string;
  cantidad: string;
}

let contador = 0;
const renglonVacio = (): RenglonPedido => ({
  llave: (contador += 1), material: null, descripcionLibre: '', cantidad: '1',
});

/**
 * Pedir material para un técnico.
 *
 * Se puede escribir algo que no esté catalogado: quien pide sabe lo que
 * le hace falta y no tiene por qué saber si está dado de alta. Almacén o
 * compras lo catalogan al atenderlo.
 */
function PedirMaterial({ onCerrar, onHecho }: { onCerrar: () => void; onHecho: () => void }) {
  const [tecnicos, setTecnicos] = useState<Opcion[]>([]);
  const [tecnicoId, setTecnicoId] = useState('');
  const [renglones, setRenglones] = useState<RenglonPedido[]>([renglonVacio()]);
  const [notas, setNotas] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [trabajando, setTrabajando] = useState(false);

  useEffect(() => {
    api.get<Opcion[]>('/tecnicos/opciones').then(setTecnicos).catch(() => undefined);
  }, []);

  const completo = (r: RenglonPedido) =>
    (r.material !== null || r.descripcionLibre.trim().length >= 3) && Number(r.cantidad) > 0;

  const listo = Boolean(tecnicoId) && renglones.every(completo);

  async function guardar() {
    setError(null);
    setTrabajando(true);
    try {
      await api.post('/materiales/solicitudes', {
        tecnicoId,
        notas: notas.trim() || undefined,
        partidas: renglones.map((r) => ({
          materialId: r.material?.id,
          descripcionLibre: r.material ? undefined : r.descripcionLibre.trim(),
          cantidad: Number(r.cantidad),
        })),
      });
      onHecho();
    } catch (e) {
      setError(e);
      setTrabajando(false);
    }
  }

  return (
    <Dialogo
      titulo="Pedir material"
      ancho
      onCerrar={onCerrar}
      pie={
        <button className="primario" disabled={!listo || trabajando} onClick={() => void guardar()}>
          {trabajando ? 'Enviando…' : 'Pedir a almacén'}
        </button>
      }
    >
      <Error error={error} />

      <div className="campo">
        <label htmlFor="tecnico">Para quién *</label>
        <select id="tecnico" value={tecnicoId} onChange={(e) => setTecnicoId(e.target.value)}>
          <option value="">Escoge al técnico</option>
          {tecnicos.map((t) => <option key={t.id} value={t.id}>{t.nombre}</option>)}
        </select>
        <span className="ayuda">
          El material sale a su nombre. Es lo que después permite saber
          cuánto lleva consumido cada quien.
        </span>
      </div>

      {renglones.map((r, i) => (
        <div key={r.llave} className="panel" style={{ marginBottom: 10 }}>
          <BuscadorMaterial
            id={`material-${r.llave}`}
            valor={r.material}
            onElegir={(m) => setRenglones((rs) => rs.map((x) => (
              x.llave === r.llave ? { ...x, material: m } : x
            )))}
          />

          {!r.material && (
            <div className="campo">
              <label htmlFor={`libre-${r.llave}`}>O descríbelo</label>
              <input
                id={`libre-${r.llave}`}
                value={r.descripcionLibre}
                placeholder="Lo que haga falta, aunque no esté en el catálogo"
                onChange={(e) => setRenglones((rs) => rs.map((x) => (
                  x.llave === r.llave ? { ...x, descripcionLibre: e.target.value } : x
                )))}
              />
            </div>
          )}

          <div className="campo">
            <label htmlFor={`cant-${r.llave}`}>Cantidad *</label>
            <input
              id={`cant-${r.llave}`}
              type="number" min="1" step="1" style={{ width: 120 }}
              value={r.cantidad}
              onChange={(e) => setRenglones((rs) => rs.map((x) => (
                x.llave === r.llave ? { ...x, cantidad: e.target.value } : x
              )))}
            />
          </div>

          {renglones.length > 1 && (
            <button
              type="button"
              onClick={() => setRenglones((rs) => rs.filter((x) => x.llave !== r.llave))}
            >
              Quitar renglón {i + 1}
            </button>
          )}
        </div>
      ))}

      <button type="button" onClick={() => setRenglones((rs) => [...rs, renglonVacio()])}>
        Agregar renglón
      </button>

      <div className="campo" style={{ marginTop: 12 }}>
        <label htmlFor="notas">Notas para almacén</label>
        <textarea id="notas" rows={2} value={notas} onChange={(e) => setNotas(e.target.value)} />
      </div>
    </Dialogo>
  );
}

/**
 * El buscador del catálogo.
 *
 * Un desplegable con el catálogo entero se hace interminable y es lo que
 * ya nos pasó con refacciones. Se escribe y se escoge del resultado.
 */
function BuscadorMaterial({ id, valor, onElegir }: {
  id: string;
  valor: { id: string; codigo: string; nombre: string; disponible: number } | null;
  onElegir: (m: { id: string; codigo: string; nombre: string; disponible: number } | null) => void;
}) {
  const [texto, setTexto] = useState('');
  const [resultados, setResultados] = useState<
    { id: string; codigo: string; nombre: string; disponible: number }[]
  >([]);

  useEffect(() => {
    if (texto.trim().length < 2) { setResultados([]); return undefined; }

    const t = setTimeout(() => {
      api.get<{ id: string; codigo: string; nombre: string; disponible: number }[]>(
        '/materiales/opciones', { busqueda: texto },
      ).then(setResultados).catch(() => setResultados([]));
    }, 250);

    return () => clearTimeout(t);
  }, [texto]);

  if (valor) {
    return (
      <div className="campo">
        <label>Material</label>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span className="mono">{valor.codigo}</span>
          <span>{valor.nombre}</span>
          <span className="conteo">{valor.disponible} disponibles</span>
          <button type="button" onClick={() => onElegir(null)}>Cambiar</button>
        </div>
      </div>
    );
  }

  return (
    <div className="campo">
      <label htmlFor={id}>Material del catálogo</label>
      <input
        id={id}
        type="search"
        value={texto}
        placeholder="Código o nombre…"
        onChange={(e) => setTexto(e.target.value)}
      />
      {resultados.length > 0 && (
        <ul style={{ listStyle: 'none', marginTop: 6 }}>
          {resultados.map((m) => (
            <li key={m.id} style={{ padding: '2px 0' }}>
              <button className="enlace" type="button" onClick={() => onElegir(m)}>
                <span className="mono">{m.codigo}</span> {m.nombre}
                <span className="conteo"> · {m.disponible} disponibles</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * La ficha: entregar lo que hay y mandar a comprar lo que no.
 *
 * Las dos cosas en la misma pantalla porque son la misma decisión, tomada
 * renglón por renglón: de esto tengo tres, de aquello nada. Separarlas
 * obligaría a recorrer la lista dos veces.
 */
function FichaSolicitud({ solicitud, surte, onCerrar, onHecho }: {
  solicitud: SolicitudMaterial;
  surte: boolean;
  onCerrar: () => void;
  onHecho: () => void;
}) {
  const [bodegas, setBodegas] = useState<Opcion[]>([]);
  const [bodegaId, setBodegaId] = useState('');
  const [entrega, setEntrega] = useState<Record<string, string>>({});
  const [aComprar, setAComprar] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<unknown>(null);
  const [trabajando, setTrabajando] = useState(false);

  useEffect(() => {
    api.get<Opcion[]>('/catalogos/bodegas/opciones')
      .then((lista) => {
        setBodegas(lista);
        if (lista.length === 1) setBodegaId(lista[0].id);
      })
      .catch(() => undefined);
  }, []);

  const pendientes = solicitud.partidas.filter((p) => p.faltante > 0);
  const hayQueEntregar = Object.values(entrega).some((c) => Number(c) > 0);
  const hayQueComprar = Object.values(aComprar).some(Boolean);

  async function entregar() {
    setError(null);
    setTrabajando(true);
    try {
      await api.post(`/materiales/solicitudes/${solicitud.id}/surtir`, {
        bodegaId,
        partidas: Object.entries(entrega)
          .filter(([, c]) => Number(c) > 0)
          .map(([partidaId, c]) => ({ partidaId, cantidad: Number(c) })),
      });
      onHecho();
    } catch (e) {
      setError(e);
      setTrabajando(false);
    }
  }

  async function pedirACompras() {
    setError(null);
    setTrabajando(true);
    try {
      await api.post(`/materiales/solicitudes/${solicitud.id}/a-compras`, {
        partidas: Object.entries(aComprar).filter(([, si]) => si).map(([id]) => id),
      });
      onHecho();
    } catch (e) {
      setError(e);
      setTrabajando(false);
    }
  }

  return (
    <Dialogo
      titulo={`${solicitud.folio} · ${solicitud.tecnico?.nombre_completo ?? '—'}`}
      ancho
      onCerrar={onCerrar}
      pie={surte && pendientes.length > 0 ? (
        <>
          <button
            className="primario"
            disabled={!hayQueEntregar || !bodegaId || trabajando}
            onClick={() => void entregar()}
          >
            {trabajando ? 'Entregando…' : 'Entregar'}
          </button>
          <button disabled={!hayQueComprar || trabajando} onClick={() => void pedirACompras()}>
            Pedir a compras
          </button>
        </>
      ) : undefined}
    >
      <Error error={error} />

      {solicitud.notas && <p className="sub">{solicitud.notas}</p>}

      {surte && pendientes.length > 0 && bodegas.length > 1 && (
        <div className="campo">
          <label htmlFor="bodega">De qué bodega sale</label>
          <select id="bodega" value={bodegaId} onChange={(e) => setBodegaId(e.target.value)}>
            <option value="">Escoge la bodega</option>
            {bodegas.map((b) => <option key={b.id} value={b.id}>{b.nombre}</option>)}
          </select>
        </div>
      )}

      <table>
        <thead>
          <tr>
            <th>Material</th>
            <th className="numero">Pidió</th>
            <th className="numero">Entregado</th>
            {surte && <th className="numero">Entregar ahora</th>}
            {surte && <th>A compras</th>}
          </tr>
        </thead>
        <tbody>
          {solicitud.partidas.map((p) => (
            <tr key={p.id}>
              <td>
                {p.material
                  ? <><span className="mono">{p.material.codigo}</span> {p.material.nombre}</>
                  : <>{p.descripcionLibre} <span className="conteo">sin catalogar</span></>}
              </td>
              <td className="numero">{p.cantidadSolicitada}</td>
              <td className="numero">{p.cantidadSurtida}</td>
              {surte && (
                <td className="numero">
                  {p.faltante === 0 ? '—' : (
                    <input
                      type="number" min="0" max={p.faltante} step="1" style={{ width: 90 }}
                      value={entrega[p.id] ?? ''}
                      onChange={(e) => setEntrega((x) => ({ ...x, [p.id]: e.target.value }))}
                    />
                  )}
                </td>
              )}
              {surte && (
                <td>
                  {p.faltante === 0 ? '—' : (
                    <input
                      type="checkbox"
                      checked={aComprar[p.id] ?? false}
                      onChange={(e) => setAComprar((x) => ({ ...x, [p.id]: e.target.checked }))}
                    />
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>

      {surte && pendientes.length > 0 && (
        <p className="ayuda" style={{ marginTop: 10 }}>
          Se puede entregar de menos: lo que falte se queda pendiente y de
          ahí sale la compra. Lo que se marque para compras se le pide a
          Compras con la cantidad que falta.
        </p>
      )}
    </Dialogo>
  );
}

// ══════════════════════════════════════════════════════════ catálogo

function Catalogo({ surte, onCambio }: { surte: boolean; onCambio: () => void }) {
  const [pagina, setPagina] = useState<Pagina<Material> | null>(null);
  const [numero, setNumero] = useState(1);
  const [busqueda, setBusqueda] = useState('');
  const [bajoMinimo, setBajoMinimo] = useState(false);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [editando, setEditando] = useState<Material | null>(null);
  const [creando, setCreando] = useState(false);
  const [moviendo, setMoviendo] = useState<{ material: Material; que: 'entrada' | 'salida' | 'ajuste' } | null>(null);
  const { orden, alClicar } = useOrden(() => setNumero(1));

  const cargar = useCallback(() => {
    setCargando(true);
    setError(null);
    api.get<Pagina<Material>>('/materiales', {
      busqueda: busqueda || undefined,
      bajoMinimo: bajoMinimo ? 'si' : undefined,
      ordenarPor: orden?.por,
      sentido: orden?.sentido,
      pagina: numero,
      limite: 25,
    })
      .then(setPagina)
      .catch(setError)
      .finally(() => setCargando(false));
  }, [busqueda, bajoMinimo, numero, orden]);

  useEffect(() => {
    const t = setTimeout(cargar, busqueda ? 300 : 0);
    return () => clearTimeout(t);
  }, [cargar, busqueda]);

  function refrescar() {
    cargar();
    onCambio();
  }

  return (
    <div className="panel">
      <div className="controles">
        <input
          type="search"
          placeholder="Código o nombre…"
          value={busqueda}
          onChange={(e) => { setBusqueda(e.target.value); setNumero(1); }}
        />
        <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <input
            type="checkbox"
            checked={bajoMinimo}
            onChange={(e) => { setBajoMinimo(e.target.checked); setNumero(1); }}
          />
          Sólo lo que hay que reponer
        </label>
        {surte && (
          <button className="primario" style={{ marginLeft: 'auto' }} onClick={() => setCreando(true)}>
            Nuevo material
          </button>
        )}
      </div>

      <Error error={error} />

      {cargando && !pagina ? <Cargando /> : pagina && (
        <>
          {pagina.data.length === 0 ? (
            <div className="vacio">No hay materiales con ese filtro.</div>
          ) : (
            <table>
              <thead>
                <tr>
                  <Ordenable por="codigo" orden={orden} alClicar={alClicar}>Código</Ordenable>
                  <Ordenable por="nombre" orden={orden} alClicar={alClicar}>Nombre</Ordenable>
                  <th>Unidad</th>
                  <th className="numero">Existencia</th>
                  <Ordenable por="minimo" orden={orden} alClicar={alClicar} primerClic="desc" className="numero">
                    Mínimo
                  </Ordenable>
                  <Ordenable por="costo" orden={orden} alClicar={alClicar} primerClic="desc" className="numero">
                    Costo
                  </Ordenable>
                  {surte && <th />}
                </tr>
              </thead>
              <tbody>
                {pagina.data.map((m) => (
                  <tr key={m.id}>
                    <td className="mono">
                      <button className="enlace" onClick={() => setEditando(m)}>{m.codigo}</button>
                    </td>
                    <td>{m.nombre}</td>
                    <td>{m.unidadMedida}</td>
                    <td className="numero">
                      <span className={m.bajoMinimo ? 'etiqueta no' : undefined}>
                        {m.existencia}
                      </span>
                    </td>
                    <td className="numero">{m.stockMinimo}</td>
                    <td className="numero">{moneda(m.costoPromedio)}</td>
                    {surte && (
                      <td className="acciones">
                        <button className="enlace" onClick={() => setMoviendo({ material: m, que: 'entrada' })}>
                          Entrada
                        </button>{' '}
                        <button className="enlace" onClick={() => setMoviendo({ material: m, que: 'salida' })}>
                          Salida
                        </button>{' '}
                        <button className="enlace" onClick={() => setMoviendo({ material: m, que: 'ajuste' })}>
                          Ajustar
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <Paginacion
            pagina={pagina.pagina} limite={pagina.limite} total={pagina.total}
            onCambiar={setNumero}
          />
        </>
      )}

      {(creando || editando) && (
        <FormularioMaterial
          material={editando}
          onCerrar={() => { setCreando(false); setEditando(null); }}
          onHecho={() => { setCreando(false); setEditando(null); refrescar(); }}
        />
      )}

      {moviendo && (
        <Movimiento
          material={moviendo.material}
          que={moviendo.que}
          onCerrar={() => setMoviendo(null)}
          onHecho={() => { setMoviendo(null); refrescar(); }}
        />
      )}
    </div>
  );
}

function FormularioMaterial({ material, onCerrar, onHecho }: {
  material: Material | null; onCerrar: () => void; onHecho: () => void;
}) {
  const [codigo, setCodigo] = useState(material?.codigo ?? '');
  const [nombre, setNombre] = useState(material?.nombre ?? '');
  const [unidad, setUnidad] = useState(material?.unidadMedida ?? 'pza');
  const [minimo, setMinimo] = useState(String(material?.stockMinimo ?? 0));
  const [error, setError] = useState<unknown>(null);
  const [trabajando, setTrabajando] = useState(false);

  const listo = codigo.trim().length >= 2 && nombre.trim().length >= 3;

  async function guardar() {
    setError(null);
    setTrabajando(true);
    try {
      const cuerpo = {
        codigo: codigo.trim(),
        nombre: nombre.trim(),
        unidadMedida: unidad.trim() || 'pza',
        stockMinimo: Number(minimo) || 0,
      };

      if (material) await api.patch(`/materiales/${material.id}`, cuerpo);
      else await api.post('/materiales', cuerpo);

      onHecho();
    } catch (e) {
      setError(e);
      setTrabajando(false);
    }
  }

  return (
    <Dialogo
      titulo={material ? `Editar ${material.codigo}` : 'Nuevo material'}
      onCerrar={onCerrar}
      pie={
        <button className="primario" disabled={!listo || trabajando} onClick={() => void guardar()}>
          {trabajando ? 'Guardando…' : 'Guardar'}
        </button>
      }
    >
      <Error error={error} />

      <div className="campo">
        <label htmlFor="codigo">Código *</label>
        <input id="codigo" value={codigo} onChange={(e) => setCodigo(e.target.value)} />
      </div>

      <div className="campo">
        <label htmlFor="nombre">Nombre *</label>
        <input id="nombre" value={nombre} onChange={(e) => setNombre(e.target.value)} />
      </div>

      <div className="campo">
        <label htmlFor="unidad">Unidad</label>
        <input id="unidad" value={unidad} onChange={(e) => setUnidad(e.target.value)} />
        <span className="ayuda">Lo que se cuenta al contarlo: pza, litro, metro, caja.</span>
      </div>

      <div className="campo">
        <label htmlFor="minimo">Mínimo</label>
        <input
          id="minimo" type="number" min="0" step="1" style={{ width: 120 }}
          value={minimo} onChange={(e) => setMinimo(e.target.value)}
        />
        <span className="ayuda">Por debajo de esto aparece en «por reponer».</span>
      </div>

      {/* El material no lleva precio de venta a propósito: no se le
          factura al cliente, es gasto. Si algún día se repercutiera,
          sería una decisión de negocio y no un campo que faltó. */}
    </Dialogo>
  );
}

/** Entrada, salida o ajuste de una sola referencia. */
function Movimiento({ material, que, onCerrar, onHecho }: {
  material: Material;
  que: 'entrada' | 'salida' | 'ajuste';
  onCerrar: () => void;
  onHecho: () => void;
}) {
  const [bodegas, setBodegas] = useState<Opcion[]>([]);
  const [tecnicos, setTecnicos] = useState<Opcion[]>([]);
  const [bodegaId, setBodegaId] = useState('');
  const [cantidad, setCantidad] = useState('');
  const [motivo, setMotivo] = useState(
    que === 'entrada' ? 'compra_proveedor' : que === 'salida' ? 'salida_tecnico' : 'ajuste_inventario',
  );
  const [tecnicoId, setTecnicoId] = useState('');
  const [costo, setCosto] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [trabajando, setTrabajando] = useState(false);

  useEffect(() => {
    api.get<Opcion[]>('/catalogos/bodegas/opciones')
      .then((lista) => { setBodegas(lista); if (lista.length === 1) setBodegaId(lista[0].id); })
      .catch(() => undefined);
    api.get<Opcion[]>('/tecnicos/opciones').then(setTecnicos).catch(() => undefined);
  }, []);

  // Entregar y recibir del técnico exigen decir de quién se trata. Lo
  // exige también la API y la base; aquí se pide antes para no mandar
  // algo que va a fallar.
  const pideTecnico = motivo === 'salida_tecnico' || motivo === 'devolucion_tecnico';
  const listo = Boolean(bodegaId)
    && (que === 'ajuste' ? cantidad !== '' : Number(cantidad) > 0)
    && (!pideTecnico || Boolean(tecnicoId))
    && (que !== 'ajuste' || observaciones.trim().length >= 5);

  async function guardar() {
    setError(null);
    setTrabajando(true);
    try {
      if (que === 'ajuste') {
        await api.post('/materiales/ajustes', {
          materialId: material.id,
          bodegaId,
          cantidadContada: Number(cantidad),
          observaciones: observaciones.trim(),
        });
      } else {
        await api.post(`/materiales/${que === 'entrada' ? 'entradas' : 'salidas'}`, {
          materialId: material.id,
          bodegaId,
          cantidad: Number(cantidad),
          motivo,
          tecnicoId: pideTecnico ? tecnicoId : undefined,
          costoUnitario: que === 'entrada' && costo !== '' ? Number(costo) : undefined,
          observaciones: observaciones.trim() || undefined,
        });
      }
      onHecho();
    } catch (e) {
      setError(e);
      setTrabajando(false);
    }
  }

  const titulo = que === 'entrada' ? 'Registrar entrada'
    : que === 'salida' ? 'Registrar salida' : 'Ajustar por conteo';

  return (
    <Dialogo
      titulo={`${titulo} · ${material.codigo}`}
      onCerrar={onCerrar}
      pie={
        <button className="primario" disabled={!listo || trabajando} onClick={() => void guardar()}>
          {trabajando ? 'Guardando…' : 'Guardar'}
        </button>
      }
    >
      <Error error={error} />

      <p className="sub">
        {material.nombre} · hay {material.existencia} {material.unidadMedida}
      </p>

      {bodegas.length > 1 && (
        <div className="campo">
          <label htmlFor="bodega">Bodega *</label>
          <select id="bodega" value={bodegaId} onChange={(e) => setBodegaId(e.target.value)}>
            <option value="">Escoge la bodega</option>
            {bodegas.map((b) => <option key={b.id} value={b.id}>{b.nombre}</option>)}
          </select>
        </div>
      )}

      {que !== 'ajuste' && (
        <div className="campo">
          <label htmlFor="motivo">Motivo *</label>
          <select id="motivo" value={motivo} onChange={(e) => setMotivo(e.target.value)}>
            {(que === 'entrada'
              ? ['compra_proveedor', 'devolucion_tecnico', 'inventario_inicial', 'otro']
              : ['salida_tecnico', 'otro']
            ).map((m) => <option key={m} value={m}>{ETIQUETA_MOTIVO[m]}</option>)}
          </select>
        </div>
      )}

      {pideTecnico && (
        <div className="campo">
          <label htmlFor="tecnico">Técnico *</label>
          <select id="tecnico" value={tecnicoId} onChange={(e) => setTecnicoId(e.target.value)}>
            <option value="">Escoge al técnico</option>
            {tecnicos.map((t) => <option key={t.id} value={t.id}>{t.nombre}</option>)}
          </select>
        </div>
      )}

      <div className="campo">
        <label htmlFor="cantidad">
          {que === 'ajuste' ? 'Lo que se contó *' : 'Cantidad *'}
        </label>
        <input
          id="cantidad" type="number" min="0" step="1" style={{ width: 140 }}
          value={cantidad} onChange={(e) => setCantidad(e.target.value)}
        />
      </div>

      {que === 'entrada' && (
        <div className="campo">
          <label htmlFor="costo">Costo unitario</label>
          <input
            id="costo" type="number" min="0" step="0.01" style={{ width: 140 }}
            value={costo} onChange={(e) => setCosto(e.target.value)}
          />
          <span className="ayuda">
            Alimenta el costo promedio. Sin él, la entrada se valúa al costo
            que ya traía.
          </span>
        </div>
      )}

      <div className="campo">
        <label htmlFor="obs">
          {que === 'ajuste' ? 'Por qué no cuadraba *' : 'Observaciones'}
        </label>
        <textarea
          id="obs" rows={2} value={observaciones}
          onChange={(e) => setObservaciones(e.target.value)}
        />
      </div>
    </Dialogo>
  );
}

// ═══════════════════════════════════════════════════════ movimientos

function Movimientos() {
  const [pagina, setPagina] = useState<Pagina<MovimientoMaterial> | null>(null);
  const [numero, setNumero] = useState(1);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    setCargando(true);
    api.get<Pagina<MovimientoMaterial>>('/materiales/movimientos', { pagina: numero, limite: 50 })
      .then(setPagina)
      .catch(setError)
      .finally(() => setCargando(false));
  }, [numero]);

  return (
    <div className="panel">
      <Error error={error} />

      {cargando && !pagina ? <Cargando /> : pagina && (
        <>
          {pagina.data.length === 0 ? (
            <div className="vacio">Todavía no hay movimientos de material.</div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Material</th>
                  <th>Motivo</th>
                  {/* La columna que justifica este inventario: a nombre
                      de quién salió. */}
                  <th>Técnico</th>
                  <th className="numero">Cantidad</th>
                  <th className="numero">Saldo</th>
                  <th>Referencia</th>
                </tr>
              </thead>
              <tbody>
                {pagina.data.map((m) => (
                  <tr key={m.id}>
                    <td>{fechaCorta(m.creadoEn)}</td>
                    <td>
                      <span className="mono">{m.material.codigo}</span> {m.material.nombre}
                    </td>
                    <td>{ETIQUETA_MOTIVO[m.motivo] ?? m.motivo}</td>
                    <td>{m.tecnico?.nombre_completo ?? '—'}</td>
                    <td className="numero">
                      <span className={m.cantidad < 0 ? 'etiqueta no' : 'etiqueta si'}>
                        {m.cantidad > 0 ? `+${m.cantidad}` : m.cantidad}
                      </span>
                    </td>
                    <td className="numero">{m.saldoResultante}</td>
                    <td>{m.referenciaFolio ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <Paginacion
            pagina={pagina.pagina} limite={pagina.limite} total={pagina.total}
            onCambiar={setNumero}
          />
        </>
      )}
    </div>
  );
}

/**
 * Cuánto material lleva cada técnico.
 *
 * Es la pregunta por la que este inventario se lleva aparte, y por la que
 * la salida guarda el técnico como columna y no como nota: se puede
 * sumar. Una devolución baja el consumo, que es lo correcto: lo que
 * regresó no se gastó.
 */
function Consumo() {
  const [datos, setDatos] = useState<ConsumoDeTecnico[] | null>(null);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    api.get<ConsumoDeTecnico[]>('/materiales/consumo').then(setDatos).catch(setError);
  }, []);

  return (
    <div className="panel">
      <Error error={error} />

      {!datos ? <Cargando /> : datos.length === 0 ? (
        <div className="vacio">Todavía no se le ha entregado material a nadie.</div>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Técnico</th>
              <th className="numero">Piezas</th>
              <th className="numero">Valor</th>
            </tr>
          </thead>
          <tbody>
            {datos.map((t) => (
              <tr key={t.tecnicoId}>
                <td>{t.tecnico}</td>
                <td className="numero">{t.piezas}</td>
                <td className="numero">{moneda(t.valor)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
